/**
 * The permission model an administrator manages: one entry per screen, grouped like the menu.
 *
 * A role is granted *pages* — `page.<id>:view`, `page.<id>:edit`, or one of a page's special
 * actions. The API itself still guards routes with its own keys (`orders:read`, `customers:manage`
 * …); those are never assigned by hand any more, they are *derived* here: each page level lists the
 * API keys its screen needs (`grants`), and `expandPermissions` turns a role's page keys into the
 * full set at sign-in. That keeps the routes untouched while the administrator sees only screens.
 *
 * Adding a page touches this file only: the role editor, the menu and the route guards all read it.
 * Shared with the backend through `@malash-office/types/page-permissions`, so no imports here.
 */

export type PageId =
  | 'planning-projects'
  | 'planning-files'
  | 'production-customers'
  | 'suppliers'
  | 'production-orders'
  | 'supplier-shipments'
  | 'supplier-invoices'
  | 'products-overview'
  | 'products'
  | 'taxonomy'
  | 'stores'
  | 'pricelists'
  | 'inventory'
  | 'warehouses'
  | 'fulfillment'
  | 'promotions'
  | 'quotes'
  | 'integrations'
  | 'subscriptions'
  | 'landing-pages'
  | 'orders'
  | 'picking'
  | 'subscription-orders'
  | 'order-mailing'
  | 'finance-overview'
  | 'collection'
  | 'payments'
  | 'customers'
  | 'users'
  | 'dashboard-users'
  | 'roles'
  | 'mail-connections'
  | 'feedback'
  | 'credit-expenses'
  | 'inbox'
  | 'whatsapp-templates'
  | 'broadcasts'
  | 'agent';

export interface PageAction {
  id: string;
  label: string;
  /** API keys this action adds on top of the page's view level. */
  grants: string[];
}

export interface PermissionPage {
  id: PageId;
  label: string;
  /** One line under the label in the role editor — what the screen is, for whoever assigns it. */
  hint?: string;
  /** API keys a role needs to open the screen. */
  view: string[];
  /** API keys added by edit. Absent when the screen has nothing to edit. */
  edit?: string[];
  actions?: PageAction[];
}

export interface PermissionGroup {
  id: string;
  label: string;
  pages: PermissionPage[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    id: 'planning',
    label: 'תכנון ופיתוח',
    pages: [
      {
        id: 'planning-projects',
        label: 'פיתוח מוצרים',
        hint: 'פרויקטים, המשימות שלי, צ׳אט וצוות',
        view: ['product-planning:read'],
        edit: ['product-planning:manage'],
        actions: [
          { id: 'budget', label: 'צפייה בתקציב', grants: ['product-planning:budget'] },
          {
            id: 'request-quote',
            label: 'שליחת בקשת הצעת מחיר לספק',
            grants: ['product-planning:request-quote']
          }
        ]
      },
      { id: 'planning-files', label: 'ספריית קבצים', view: ['product-planning:read'] },
      {
        id: 'production-customers',
        label: 'לקוחות ייצור',
        view: ['production-customers:read'],
        edit: ['production-customers:manage']
      },
      { id: 'suppliers', label: 'ספקים', view: ['product-planning:read'], edit: ['product-planning:manage'] }
    ]
  },
  {
    id: 'production',
    label: 'יצור ורכש',
    pages: [
      {
        id: 'production-orders',
        label: 'הזמנות ספקים',
        view: ['production-orders:read'],
        edit: ['production-orders:manage'],
        actions: [
          { id: 'send', label: 'שליחה לספק', grants: ['production-orders:send'] },
          { id: 'budget', label: 'צפייה בתקציב', grants: ['production-orders:budget'] }
        ]
      },
      {
        id: 'supplier-shipments',
        label: 'שילוח ספקים',
        hint: 'משלוחים אוויריים וימיים בדרך, מכולות, וקליטה למחסן',
        view: ['supplier-shipments:read'],
        // Receiving a shipment writes stock into the target warehouse; that is part of moving its
        // status, so it rides on edit rather than on a separate action.
        edit: ['supplier-shipments:manage']
      },
      {
        id: 'supplier-invoices',
        label: 'חשבוניות ספקים',
        hint: 'חשבוניות שהתקבלו מספקים, תשלומים, שיוך לפרויקטים וסקירה כספית',
        view: ['supplier-invoices:read'],
        // Recording a payment and approving an invoice ride on edit. They are finance actions and
        // the screen is admin-only for now; splitting them into actions is a later, role-level call.
        edit: ['supplier-invoices:manage']
      }
    ]
  },
  {
    id: 'products',
    label: 'ניהול מוצרים',
    pages: [
      { id: 'products-overview', label: 'לוח בקרה', view: ['products:read'] },
      {
        id: 'products',
        label: 'מוצרים',
        hint: 'קטלוג המוצרים וכרטיס מוצר',
        view: ['products:read', 'catalog:read', 'audit:read'],
        edit: ['products:manage']
      },
      {
        id: 'taxonomy',
        label: 'קטגוריות ומאפיינים',
        hint: 'קטגוריות, מותגים, יצרנים ומאפיינים',
        view: ['catalog:read'],
        edit: ['catalog:manage']
      },
      { id: 'stores', label: 'חנויות', view: ['stores:read'], edit: ['stores:manage'] },
      { id: 'pricelists', label: 'מחירונים', view: ['prices:read'], edit: ['prices:manage'] },
      {
        id: 'inventory',
        label: 'מלאי',
        hint: 'עריכה = מדיניות מלאי של מוצר',
        view: ['inventory:read'],
        edit: ['inventory:manage'],
        actions: [
          { id: 'adjust', label: 'עדכון כמויות', grants: ['inventory:adjust'] },
          { id: 'transfer', label: 'העברה בין מחסנים', grants: ['inventory:transfer'] }
        ]
      },
      { id: 'warehouses', label: 'מחסנים', view: ['warehouses:read'], edit: ['warehouses:manage'] },
      { id: 'fulfillment', label: 'שילוח ואספקה', view: ['shipping:read'], edit: ['shipping:manage'] },
      { id: 'promotions', label: 'מבצעים', view: ['promotions:read'], edit: ['promotions:manage'] },
      {
        id: 'quotes',
        label: 'הצעות מחיר',
        hint: 'הצעות מחיר ללקוחות, חתימה דיגיטלית, המרה להזמנה ודפי בקשת הצעה',
        // The builder names the store and its currency; the convert dialog lists shipping methods.
        view: ['quotes:read', 'stores:read'],
        // Converting a signed quote creates the order itself, so it rides on edit — a rep who may
        // send a quote for signature must be able to act on the signature.
        edit: ['quotes:manage', 'shipping:read']
      },
      { id: 'integrations', label: 'אינטגרציות', view: ['integrations:read'], edit: ['integrations:manage'] },
      {
        id: 'subscriptions',
        label: 'ניהול מנויים ותוכניות',
        view: ['subscriptions:read'],
        edit: ['subscriptions:manage']
      },
      {
        id: 'landing-pages',
        label: 'דפי נחיתה',
        hint: 'דפים ציבוריים למכירת מנויים, תבניות עיצוב והגדרות צ׳קאאוט',
        // The editor picks from the subscription plans, and — for a products page — from a store's
        // pricebook, so viewing a page also needs to read those.
        view: ['landingPages:read', 'subscriptions:read', 'stores:read', 'prices:read'],
        edit: ['landingPages:manage']
      }
    ]
  },
  {
    id: 'warehouse',
    label: 'מחסן והזמנות',
    pages: [
      {
        id: 'orders',
        label: 'הזמנות',
        hint: 'הזמנות מכל החנויות',
        view: ['orders:read', 'audit:read'],
        edit: ['orders:manage']
      },
      { id: 'picking', label: 'ליקוט הזמנות', view: ['orders:pick'] },
      {
        id: 'subscription-orders',
        label: 'הזמנות מנויים',
        hint: 'כולל תצוגת מחסן וזיכויים',
        view: ['subscription-orders:read', 'subscription-credits:read'],
        edit: ['subscription-orders:manage', 'subscription-credits:manage']
      }
    ]
  },
  {
    // The mockup's "תקשורת ולקוחות" (docs/inbox/PLAN.md §9): the inbox now, broadcasts later.
    id: 'communication',
    label: 'תקשורת ולקוחות',
    pages: [
      {
        // תיבת פניות (docs/inbox/PLAN.md §8). Every conversation is visible to whoever holds the page —
        // domains classify, they do not restrict (2026-10-28). Edit answers the customer: it sends a
        // WhatsApp message on the organisation's number.
        id: 'inbox',
        label: 'שירות לקוחות',
        hint: 'תיבת הפניות: פניות מלקוחות בכל הערוצים (WhatsApp ובהמשך מייל וטלפון), רשימה, שיחה ומענה',
        view: ['inbox:read'],
        edit: ['inbox:reply'],
        // Erasing a customer's conversations and files on request — irreversible, so its own action.
        actions: [{ id: 'delete-data', label: 'מחיקת מידע של לקוח (לפי בקשה)', grants: ['inbox:delete-data'] }]
      },
      {
        // The templates Meta must approve before a message may open a conversation (PLAN §3.6). Edit
        // changes them at Meta: submitting a new one for review, syncing, deleting.
        id: 'whatsapp-templates',
        label: 'תבניות WhatsApp',
        hint: 'יצירת תבניות הודעה, שליחתן לאישור Meta ומעקב אחר הסטטוס (מאושרת, ממתינה, נדחתה)',
        view: ['whatsapp-templates:read'],
        edit: ['whatsapp-templates:manage']
      },
      {
        // WhatsApp broadcasts (PLAN §8). Edit prepares drafts; sending is its own action — it reaches
        // thousands at once and cannot be recalled.
        id: 'broadcasts',
        label: 'דיוורי WhatsApp',
        hint: 'דיוור תבנית WhatsApp לקהל: אשף, קהל, תזמון, מעקב ותגובות',
        view: ['broadcasts:read'],
        edit: ['broadcasts:manage'],
        actions: [{ id: 'send', label: 'שליחת דיוור (בפועל)', grants: ['broadcasts:send'] }]
      }
    ]
  },
  {
    id: 'customer-service',
    label: 'שירות לקוחות',
    pages: [
      {
        id: 'order-mailing',
        label: 'דיוור הזמנות',
        view: ['order-mailing:read'],
        edit: ['order-mailing:manage']
      }
    ]
  },
  {
    id: 'finance',
    label: 'הנהלת חשבונות וכספים',
    pages: [
      { id: 'finance-overview', label: 'סקירה', view: ['customers:read'] },
      { id: 'collection', label: 'ניהול גביה', view: ['customers:read'], edit: ['customers:manage'] },
      { id: 'payments', label: 'תשלומים', view: ['payments:read'] },
      {
        id: 'customers',
        label: 'לקוחות',
        hint: 'רשימת לקוחות, כרטיס לקוח, הערות ומשימות',
        view: ['customers:read'],
        edit: ['customers:manage']
      }
    ]
  },
  {
    id: 'system',
    label: 'ניהול מערכת',
    pages: [
      { id: 'users', label: 'משתמשים', view: ['users:read'], edit: ['users:manage'] },
      {
        // The users live in sm-dashboard; this screen reads and writes them through its backend
        // (docs/product-planning/DASHBOARD-USERS.md). Deleting a user and payment-provider
        // credentials ride on edit, like every other write on the card. Sending a mailing is its
        // own action: it reaches many people at once and cannot be recalled.
        id: 'dashboard-users',
        label: 'משתמשי דשבורד',
        hint: 'המשתמשים של sm-dashboard: רשימה, כרטיס משתמש, תגיות פנימיות, לוג פעילות ודיוור',
        view: ['dashboard-users:read'],
        edit: ['dashboard-users:manage'],
        actions: [{ id: 'mail', label: 'שליחת דיוור למשתמשים', grants: ['dashboard-users:mail'] }]
      },
      { id: 'roles', label: 'תפקידים והרשאות', view: ['roles:read'], edit: ['roles:manage'] },
      // No edit level: the API has no read-only view of mail settings — even listing them needs
      // `mailing:manage` — so opening either screen is already managing it.
      {
        // One screen for every mail connection (docs/product-planning/MAIL-CONNECTIONS.md): the
        // employees' personal mailboxes, the אגפים (Smoove senders, `mailing:manage`), and later
        // the customer-service mailboxes. Connecting one's *own* mailbox needs no page — it is in
        // the personal area, open to everybody.
        id: 'mail-connections',
        label: 'חיבור מיילים',
        hint: 'מיילים אישיים של העובדים (מי חיבר ומה הסטטוס), מיילי אגפים לדיוור ומיילי שירות לקוחות',
        view: ['mail-connections:manage', 'mailing:manage']
      },
      {
        // The manager's side of פניות ומשוב. Filing and following one's own requests needs no page:
        // every staff member holds `feedback:submit` (`STAFF_BASELINE_KEYS`).
        id: 'feedback',
        label: 'ניהול פניות ומשוב',
        hint: 'כל הפניות של המשתמשים: סטטוס, דחיפות, תגובות, הערות פנימיות ומיזוג כפילויות',
        view: ['feedback:manage']
      },
      {
        // The office's side of הוצאות אשראי. Filing a request and following it needs no page: every
        // staff member holds `credit-expenses:submit` (`STAFF_BASELINE_KEYS`). View reads every request,
        // the cards and the subscriptions; edit approves, pays, rejects and keeps the cards.
        id: 'credit-expenses',
        label: 'הוצאות אשראי',
        hint: 'בקשות תשלום באשראי של כל העובדים: אישור, תשלום, חשבוניות חסרות, כרטיסי אשראי ומנויים קבועים',
        view: ['credit-expenses:read'],
        edit: ['credit-expenses:manage']
      }
    ]
  },
  {
    id: 'general',
    label: 'כללי',
    pages: [
      {
        id: 'agent',
        label: 'עוזר AI (צ׳אט)',
        hint: 'הצ׳אט פועל רק בתוך ההרשאות שיש למשתמש',
        view: ['agent:use'],
        actions: [{ id: 'review', label: 'סקירת שיחות הצ׳אט', grants: ['agent:review'] }]
      }
    ]
  }
];

// `reduce` rather than `flatMap`: this lib compiles against es2017, which has no `flatMap`.
export const ALL_PERMISSION_PAGES: PermissionPage[] = PERMISSION_GROUPS.reduce<PermissionPage[]>(
  (pages, group) => pages.concat(group.pages),
  []
);

export const pageView = (id: PageId) => `page.${id}:view`;
export const pageEdit = (id: PageId) => `page.${id}:edit`;
export const pageAction = (id: PageId, action: string) => `page.${id}:${action}`;

/** Every page key that exists — what the role editor may assign and what `*` stands for. */
export const ALL_PAGE_KEYS: string[] = ALL_PERMISSION_PAGES.reduce<string[]>(
  (keys, page) =>
    keys.concat(
      pageView(page.id),
      page.edit ? [pageEdit(page.id)] : [],
      (page.actions ?? []).map(action => pageAction(page.id, action.id))
    ),
  []
);

export const isPageKey = (key: string) => key.startsWith('page.');

/**
 * Turns the keys a role holds into everything its holder is allowed: the page keys themselves
 * (edit and actions imply view), plus the API keys each grants. `*` means every page. Keys that
 * are not page keys (`portal:use`, `*`) pass through unchanged.
 */
/**
 * Keys every staff member holds, whatever their roles grant — filing a פנייה ומשוב is open to
 * everybody who works in the system (2026-10-21), and so is asking for a credit-card payment
 * (הוצאות אשראי, 2026-10-26). "Staff" is anyone holding a key other than
 * `portal:use`: a portal-only customer gets nothing extra, and a user with no roles at all has no
 * key to begin with.
 */
export const STAFF_BASELINE_KEYS = ['feedback:submit', 'credit-expenses:submit'];

export const expandPermissions = (roleKeys: string[]): string[] => {
  const held = new Set(roleKeys.includes('*') ? [...roleKeys, ...ALL_PAGE_KEYS] : roleKeys);
  const result = new Set(roleKeys);
  const add = (keys: string[]) => keys.forEach(key => result.add(key));

  for (const page of ALL_PERMISSION_PAGES) {
    const editKey = pageEdit(page.id);
    const heldActions = (page.actions ?? []).filter(action => held.has(pageAction(page.id, action.id)));
    const canEdit = !!page.edit && held.has(editKey);

    if (!held.has(pageView(page.id)) && !canEdit && !heldActions.length) {
      continue;
    }
    add([pageView(page.id), ...page.view]);
    if (canEdit) {
      add([editKey, ...(page.edit ?? [])]);
    }
    heldActions.forEach(action => add([pageAction(page.id, action.id), ...action.grants]));
  }

  if ([...result].some(key => key !== 'portal:use')) add(STAFF_BASELINE_KEYS);

  return [...result];
};
